import { useAgentStepData } from "@/modules/agent/states";
import { getCustomizeControlsActions } from "@/modules/agent/states/cusomize-controls";
import { VendorRiskSectionEnum } from "@/modules/agent/types/vendorAssessment";
import { BreachHistoryEvent, CompanyDrawerData, ComplianceItem, DarkWebChatterFinding, NewsArticle } from "@/modules/agent/types/vendorCompanyData";
import { VendorOverviewKey, VendorOverviewKeyToValueTypeMap } from "@/modules/agent/types/vendorOverviewSection";
import { AgentSessionStatus, AgentSessionStepType } from "@/modules/sessions/types";
import { Card } from "@nextui-org/react";
import { Newspaper, ShieldCheck, Unlink, VenetianMask } from "lucide-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { TextAreaCard } from "../Overview/OverviewCards";
import { useVendorRiskAssessment } from "../useVendorRiskAssessment/useVendorRiskAssessment";
import { useVendorRiskAssessmentStore } from "../useVendorRiskAssessment/useVendorRiskAssessmentStore";
import CompanyDrawer from "./CompanyDrawer";

interface IdParams {
  id: string;
}

const Company: React.FC = () => {
  // Get session data
  const { id } = useParams<keyof IdParams>() as IdParams;

  // Get data from the vendor risk assessment store
  const {
    vendorOverview,
    vendorCompanyData // Use this instead of custom companyData
  } = useVendorRiskAssessment();
  const setVendorOverview = useVendorRiskAssessmentStore().setVendorOverview;
  const setVendorCompanyData = useVendorRiskAssessmentStore().setVendorCompanyData;
  const stepData = useAgentStepData(id);
  const isReportReady =
    stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT)
      ?.status === AgentSessionStatus.COMPLETE;

  // State for managing which drawer is open
  const [openDrawer, setOpenDrawer] = useState<string | null>(null);

  // Helper function to get company data by key
  // Updated getCompanyValue function with full backward compatibility
  const getCompanyValue = (key: string) => {
    try {
      // Backward compatibility - check for company_information in vendorOverview
      if (key === "company_information") {
        // Get from vendorOverview if available
        const companyDesc = vendorOverview.find(item => item.key === "company_description");
        if (companyDesc?.value?.value) {
          return companyDesc.value.value;
        }
        // Also check vendorCompanyData for the new structure
        const item = vendorCompanyData.find(item => item.key === key);
        if (item?.value?.company_information) {
          return item.value.company_information;
        }
        // Return empty string instead of falling through to "-"
        return "";
      }

      // Backward compatibility - check for services_and_products in vendorOverview
      if (key === "services_and_products") {
        // Get from vendorOverview if available
        const serviceDesc = vendorOverview.find(item => item.key === "description_of_services");
        if (serviceDesc?.value?.value) {
          return serviceDesc.value.value;
        }
        // Also check vendorCompanyData for the new structure
        const item = vendorCompanyData.find(item => item.key === key);
        if (item?.value?.services_and_products) {
          return item.value.services_and_products;
        }
        // Return empty string instead of falling through to "-"
        return "";
      }

      // Rest of your existing function remains the same
      const item = vendorCompanyData.find(item => item.key === key);
      
      if (!item || !item.value) return "-";

      // If it's one of the nested fields in company_overview
      if (key === "company_name") return item.value.company_overview?.name || "-";
      if (key === "founding_date") return item.value.company_overview?.founded || "-";
      if (key === "headquarters_location") return item.value.company_overview?.headquarters || "-";
      if (key === "ownership_structure") return item.value.company_overview?.ownership || "-";
      if (key === "employee_count") return item.value.company_overview?.no_of_employees || "-";
      if (key === "website_domain") return item.value.company_overview?.primary_domain || "-";
      // For compliance, breach history, etc.
      if (key === "breach_history_events") return item.value.breach_history_events?.length.toString() || "0";
      if (key === "dark_web_chatter_findings") return item.value.dark_web_chatter_findings?.length.toString() || "0";
      if (key === "compliances") return item.value.compliances?.length.toString() || "0";
      if (key === "news_articles") return item.value.news_articles?.length.toString() || "0";

      // For other simple string values
      if (typeof item.value === 'string') return item.value;
      if (typeof item.value.value === 'string') return item.value.value;

      return "-";
    } catch (error) {
      console.error("Error retrieving company value:", error);
      return "-";
    }
  };

  // Add this helper function to determine where the data is coming from
  const getDataSource = (key: string) => {
    // For company_information
    if (key === "company_information") {
      // Check if data exists in new structure
      const hasNewStructure = vendorCompanyData.some(item =>
        item.key === "company_overview" &&
        item.value?.company_information
      );

      // Check if data exists in legacy structure
      const hasLegacyStructure = vendorOverview.some(item =>
        item.key === "company_description" &&
        item.value?.value
      );

      // Return the appropriate source
      return hasNewStructure ? "company_data" : (hasLegacyStructure ? "overview" : "company_data");
    }

    // For services_and_products
    if (key === "services_and_products") {
      const hasNewStructure = vendorCompanyData.some(item =>
        item.key === "services_and_products" &&
        item.value?.services_and_products
      );

      const hasLegacyStructure = vendorOverview.some(item =>
        item.key === "description_of_services" &&
        item.value?.value
      );


      return hasNewStructure ? "company_data" : (hasLegacyStructure ? "overview" : "company_data");
    }

    // For all other fields, always use company_data
    return "company_data";
  };

  // First, update your getDrawerData function to use generics for better type safety:
  const getDrawerData = <T extends BreachHistoryEvent | DarkWebChatterFinding | ComplianceItem | NewsArticle>(
    key: string
  ): T[] => {
    const item = vendorCompanyData.find(item => item.key === key);
    if (!item || !item.value) return [] as T[];

    if (key === "breach_history_events") return (item.value.breach_history_events || []) as T[];
    if (key === "dark_web_chatter_findings") return (item.value.dark_web_chatter_findings || []) as T[];
    if (key === "compliances") return (item.value.compliances || []) as T[];
    if (key === "news_articles") return (item.value.news_articles || []) as T[];

    return [] as T[]; // Default empty array if no match
  };

  // Function to open a drawer
  const handleOpenDrawer = (drawerType: string) => {
    setOpenDrawer(drawerType);
  };

  // Function to close the drawer
  const handleCloseDrawer = () => {
    setOpenDrawer(null);
  };
  
  // Socket hooks removed - now managed at parent VendorRiskAssessment level to prevent
  // duplicate connections when switching between tabs

  // Implement the logic to handle field value changes
  const handleFieldValueChange = (
    fieldKey: string,
    valueKey: string,
    newValue: string
  ) => {
    const newData = [...vendorCompanyData];
    if (fieldKey === "company_information" || fieldKey === "services_and_products") {
      // For company information and services fields, update in company_overview
      const indexOfCompanyData = newData.findIndex(item => item.key === fieldKey);
      if (indexOfCompanyData > -1 && newData[indexOfCompanyData].value) {
        // Create a new company_overview object to maintain immutability
        newData[indexOfCompanyData].value = {
          ...newData[indexOfCompanyData].value,
          [fieldKey]: newValue
        };
      }
    } else {
      // For other fields
      const indexOfCompanyData = newData.findIndex(item => item.key === fieldKey);

      if (indexOfCompanyData > -1) {
        if (newData[indexOfCompanyData].value && typeof newData[indexOfCompanyData].value === 'object') {
          newData[indexOfCompanyData].value.value = newValue;
        } else {
          newData[indexOfCompanyData].value = {
            value: newValue,
            compliances: [],
            breach_history_events: [],
            dark_web_chatter_findings: [],
            news_articles: []
          };
        }
      }
    }

    // Get store actions
    const store = useVendorRiskAssessmentStore.getState();

    // Set saving state and update overview data
    store.setIsSaving(true);
    setVendorCompanyData(newData);

    // Update combinedVendorRiskData as well
    if (store.combinedVendorRiskData) {
      const updatedCombinedData = {
        ...store.combinedVendorRiskData
      };

      updatedCombinedData[VendorRiskSectionEnum.company_profile] = newData as any;
      store.setCombinedVendorRiskData(updatedCombinedData);
    }

    // To explicitly trigger changes
    getCustomizeControlsActions().setChangesMade((prev: number) => prev + 1);

    // Saving state will be reset when socket "shared:doc:updated" event is received
  };

  // Prepare data for the drawers - with complete arrays for detailed viewing
  const companyDataForDrawers: CompanyDrawerData = {
    name: getCompanyValue("company_name"),
    headquarters: getCompanyValue("headquarters_location"),
    ownership: getCompanyValue("ownership_structure"),
    founded: getCompanyValue("founding_date"),
    employees: getCompanyValue("employee_count"),
    primaryDomain: getCompanyValue("website_domain"),
    companyInformation: getCompanyValue("company_information"),
    servicesAndProducts: getCompanyValue("services_and_products"),

    // Include the full arrays, not just counts, with proper type assertions
    breachHistory: {
      count: parseInt(getCompanyValue("breach_history_events") || "0"),
      label: "events",
      items: getDrawerData<BreachHistoryEvent>("breach_history_events"),
    },
    darkWebChatter: {
      count: parseInt(getCompanyValue("dark_web_chatter_findings") || "0"),
      label: "findings",
      items: getDrawerData<DarkWebChatterFinding>("dark_web_chatter_findings"),
    },
    compliance: {
      count: parseInt(getCompanyValue("compliances") || "0"),
      label: "certifications",
      items: getDrawerData<ComplianceItem>("compliances"),
    },
    news: {
      count: parseInt(getCompanyValue("news_articles") || "0"),
      label: "articles",
      items: getDrawerData<NewsArticle>("news_articles"),
    }
  };

  // Extract company overview data from vendorCompanyData
  const companyOverview = (() => {
    const item = vendorCompanyData.find(item => item.key === "company_overview");
    return item?.value?.company_overview || {};
  })();

  const handleFieldValueChangeBackwardCompatibility = <
    K extends VendorOverviewKey,
    V extends keyof VendorOverviewKeyToValueTypeMap[K]
  >(
    fieldKey: K,
    valueKey: V,
    newValue: any
  ) => {
    const newVendorOverview = [...vendorOverview];
    const indexOfOverviewItem = vendorOverview.findIndex(
      (overviewItem) => overviewItem.key === fieldKey
    );
    if (indexOfOverviewItem > -1) {
      (newVendorOverview[indexOfOverviewItem]
        .value as VendorOverviewKeyToValueTypeMap[K])[valueKey] = newValue;
    }

    // Get store actions
    const store = useVendorRiskAssessmentStore.getState();

    // Set saving state and update overview data
    store.setIsSaving(true);
    setVendorOverview(newVendorOverview);

    // Update combinedVendorRiskData as well
    if (store.combinedVendorRiskData) {
      const updatedCombinedData = {
        ...store.combinedVendorRiskData,
        [VendorRiskSectionEnum.vendor_overview]: newVendorOverview
      };
      store.setCombinedVendorRiskData(updatedCombinedData);
    }

    // to explicitly trigger changes
    // to explicitly trigger changes
    getCustomizeControlsActions().setChangesMade((prev) => prev + 1);
    
    // Saving state will be reset when socket "shared:doc:updated" event is received
  };

  // Then use it directly in the UI
  return (
    <div className="p-6 flex flex-col gap-6">
      <Card className="w-full p-4 bg-[#FAFAFA] relative group shadow-none hover:shadow-small focus-within:shadow-small rounded-lg">
        <div className="flex flex-col">
          <h4 className="text-sm font-medium mb-6 text-[#171717]">Company Profile</h4>

          {/* Company Profile Information */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="mb-4 flex">
                <div className="text-sm text-[#777780] w-1/3 min-w-[100px]">Name</div>
                <div className="text-sm text-[#171717] flex-1 break-words">{companyOverview.name || '-'}</div>
              </div>
              <div className="mb-4 flex">
                <div className="text-sm text-[#777780] w-1/3 min-w-[100px]">Headquarters</div>
                <div className="text-sm text-[#171717] flex-1 break-words">{companyOverview.headquarters || '-'}</div>
              </div>
              <div className="flex">
                <div className="text-sm text-[#777780] w-1/3 min-w-[100px]">Ownership</div>
                <div className="text-sm text-[#171717] flex-1 break-words">{companyOverview.ownership || '-'}</div>
              </div>
            </div>

            <div>
              <div className="mb-4 flex">
                <div className="text-sm text-[#777780] w-1/3 min-w-[100px]">Founded</div>
                <div className="text-sm text-[#171717] flex-1 break-words">{companyOverview.founded || '-'}</div>
              </div>
              <div className="mb-4 flex">
                <div className="text-sm text-[#777780] w-1/3 min-w-[100px]">Employees</div>
                <div className="text-sm text-[#171717] flex-1 break-words">{companyOverview?.no_of_employees || 'No Data Available'}</div>
              </div>
              <div className="flex">
                <div className="text-sm text-[#777780] w-1/3 min-w-[100px]">Primary Domain</div>
                <div className="text-sm text-[#171717] flex-1 break-words">
                  {companyOverview.primary_domain ? (
                    <a
                      href={`https://${companyOverview.primary_domain}`}
                      className="text-[#006FEE] hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {companyOverview.primary_domain}
                    </a>
                  ) : (
                    '-'
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Stats Icons Section */}
          <div className="mt-6 pt-6 border-t border-[#E4E4E7]">
            <div className="grid grid-cols-4 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-1 pt-0">
                  <Unlink size={24} color="#A1A1AA" />
                </div>
                <div>
                  <p className="text-sm text-[#777780] mb-1">Breach History</p>
                  {parseInt(getCompanyValue("breach_history_events")) > 0 ? (
                    <p
                      onClick={() => handleOpenDrawer('breachHistory')}
                      className="text-sm text-[#006FEE] cursor-pointer flex items-center"
                    >
                      <span>
                        {`${parseInt(getCompanyValue("breach_history_events"))} events`}
                      </span>
                    </p>
                  ) : (
                    <span className="text-sm text-[#A1A1AA]">-</span>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-1 pt-0">
                  <VenetianMask size={24} color="#A1A1AA" />
                </div>
                <div>
                  <p className="text-sm text-[#777780] mb-1">Dark Web Chatter</p>
                  {parseInt(getCompanyValue("dark_web_chatter_findings") || "0") > 0 ? (
                    <p
                      onClick={() => handleOpenDrawer('darkWebChatter')}
                      className="text-sm text-[#006FEE] cursor-pointer flex items-center"
                    >
                      <span>
                        {`${parseInt(getCompanyValue("dark_web_chatter_findings"))} findings`}
                      </span>
                    </p>
                  ) : (
                    <span className="text-sm text-[#A1A1AA]">-</span>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-1 pt-0">
                  <ShieldCheck size={24} color="#A1A1AA" />
                </div>
                <div>
                  <p className="text-sm text-[#777780] mb-1">Compliance</p>
                  {parseInt(getCompanyValue("compliances") || "0") > 0 ? (
                    <p
                      onClick={() => handleOpenDrawer('compliance')}
                      className="text-sm text-[#006FEE] cursor-pointer flex items-center"
                    >
                      <span>
                        {`${parseInt(getCompanyValue("compliances"))} certifications`}
                      </span>
                    </p>
                  ) : (
                    <span className="text-sm text-[#A1A1AA]">-</span>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-1 pt-0">
                  <Newspaper size={24} color="#A1A1AA" />
                </div>
                <div>
                  <p className="text-sm text-[#777780] mb-1">News</p>
                  {parseInt(getCompanyValue("news_articles") || "0") > 0 ? (
                    <p
                      onClick={() => handleOpenDrawer('news')}
                      className="text-sm text-[#006FEE] cursor-pointer flex items-center"
                    >
                      <span>
                        {`${parseInt(getCompanyValue("news_articles"))} articles`}
                      </span>
                    </p>
                  ) : (
                    <span className="text-sm text-[#A1A1AA]">-</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-6 mt-2">
        <TextAreaCard
          title="Company Information"
          handleTextAreaValueChange={(newValue: any) =>
            getDataSource("company_information") === "overview"
              ? handleFieldValueChangeBackwardCompatibility("company_description", "value", newValue)
              : handleFieldValueChange("company_information", "value", newValue)
          }
          textAreaValue={getCompanyValue("company_information")}
          isDisabled={isReportReady}
          sessionId={id || ""}
          controlId={getDataSource("company_information") === "overview" ? "company_info" : "company_information"}
          placeholder="Add details about the company"
        />

        <TextAreaCard
          title="Services and Products"
          handleTextAreaValueChange={(newValue: any) =>
            getDataSource("services_and_products") === "overview"
              ? handleFieldValueChangeBackwardCompatibility("description_of_services", "value", newValue)
              : handleFieldValueChange("services_and_products", "value", newValue)
          }
          textAreaValue={getCompanyValue("services_and_products")}
          isDisabled={isReportReady}
          sessionId={id || ""}
          controlId={getDataSource("services_and_products") === "overview" ? "product_service_info" : "services_and_products"}
          placeholder="Add details about the product or service"
        />
      </div>

      {openDrawer && (
        <CompanyDrawer 
          isOpen={openDrawer !== null}
          drawerType={openDrawer} 
          onClose={handleCloseDrawer}
          companyData={companyDataForDrawers}
        />
      )}
    </div>
  );
};

export default Company;